export type AppearanceTransitionFrame = {
  readonly previous: HTMLIFrameElement;
  readonly replacement: HTMLIFrameElement;
  commit(): void;
  rollback(): void;
};

export type PreparedAppearanceTransition = Pick<
  AppearanceTransitionFrame,
  'commit' | 'rollback'
>;

type PreviousFrameState = {
  pointerEvents: string;
  inert: boolean;
  ariaBusy: string | null;
};

type ParentFrameState = {
  position: string;
  establishesContainingBlock: boolean;
};

/**
 * Stages a replacement iframe without exposing it or removing the last
 * successful render. Both generations are inert until the caller commits or
 * rolls back the transition.
 */
export function stageAppearanceTransitionFrame(
  previous: HTMLIFrameElement,
  replacement: HTMLIFrameElement,
): AppearanceTransitionFrame {
  const parent = previous.parentNode;
  if (!parent) throw new Error('The payment component is not mounted.');

  const previousState: PreviousFrameState = {
    pointerEvents: previous.style.pointerEvents,
    inert: previous.inert,
    ariaBusy: previous.getAttribute('aria-busy'),
  };
  const parentState: ParentFrameState = {
    position: parent instanceof HTMLElement ? parent.style.position : '',
    establishesContainingBlock:
      parent instanceof HTMLElement &&
      typeof window.getComputedStyle === 'function' &&
      window.getComputedStyle(parent).position !== 'static',
  };
  const replacementTransition = replacement.style.transition;
  const replacementOpacity = replacement.style.opacity;
  const replacementId = replacement.id;
  replacement.id = `${replacementId}-appearance-preview`;
  replacement.dataset.paypercutAppearanceTransition = 'replacement';
  replacement.setAttribute('aria-hidden', 'true');
  replacement.inert = true;
  replacement.style.position = 'absolute';
  replacement.style.inset = '0';
  // Keep the document renderable while it initializes. Real Hosted Elements can
  // stall before publishing resize/ready when their iframe is visibility:hidden.
  // Opacity preserves the atomic visual swap without suppressing that lifecycle.
  replacement.style.opacity = '0';
  replacement.style.pointerEvents = 'none';
  replacement.style.transition = 'none';

  try {
    // Absolute staging must remain scoped to the merchant-provided mount target.
    // Without a containing block, the transparent replacement can initialize at
    // viewport width, preventing Hosted Elements from reaching Interactive Ready.
    if (parent instanceof HTMLElement && !parentState.establishesContainingBlock) {
      parent.style.position = 'relative';
    }
    parent.appendChild(replacement);
    previous.dataset.paypercutAppearanceTransition = 'previous';
    previous.setAttribute('aria-busy', 'true');
    previous.inert = true;
    previous.style.pointerEvents = 'none';
  } catch (error) {
    replacement.remove();
    replacement.id = replacementId;
    if (parent instanceof HTMLElement && !parentState.establishesContainingBlock) {
      parent.style.position = parentState.position;
    }
    throw error;
  }

  let settled = false;
  const restorePrevious = (): void => {
    delete previous.dataset.paypercutAppearanceTransition;
    if (previousState.ariaBusy === null) previous.removeAttribute('aria-busy');
    else previous.setAttribute('aria-busy', previousState.ariaBusy);
    previous.inert = previousState.inert;
    previous.style.pointerEvents = previousState.pointerEvents;
  };
  const restoreParent = (): void => {
    if (parent instanceof HTMLElement && !parentState.establishesContainingBlock) {
      parent.style.position = parentState.position;
    }
  };
  const activateReplacement = (): void => {
    replacement.id = replacementId;
    delete replacement.dataset.paypercutAppearanceTransition;
    replacement.removeAttribute('aria-hidden');
    replacement.inert = false;
    replacement.style.position = '';
    replacement.style.inset = '';
    replacement.style.opacity = replacementOpacity;
    replacement.style.pointerEvents = '';
    // Commit the measured geometry before restoring ordinary resize animation.
    // Otherwise a freshly visible frame animates from its initial zero height.
    replacement.getBoundingClientRect();
    replacement.style.transition = replacementTransition;
  };

  return {
    previous,
    replacement,
    commit() {
      if (settled) return;
      settled = true;
      previous.remove();
      activateReplacement();
      restoreParent();
    },
    rollback() {
      if (settled) return;
      settled = true;
      replacement.remove();
      restorePrevious();
      restoreParent();
    },
  };
}
